import { useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { checkIsAdminWithRefresh } from '../lib/adminCheck'
import { EN_PREFIX, langFromPath } from '../i18n/urlLang'
import { AuthContext, type AdminRole, type AuthContextValue } from './AuthContext'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState<AdminRole>(null)
  const [roleLoading, setRoleLoading] = useState(false)

  useEffect(() => {
    // 1. Hydrate initial session (restore depuis localStorage).
    //    .catch fail-open: en cas d'erreur réseau au boot, on considère l'user
    //    comme anonyme; onAuthStateChange pourra recover si la session existe côté Supabase.
    //    .finally garantit que setLoading(false) fire TOUJOURS (sinon l'app hang indéfiniment).
    supabase.auth.getSession()
      .then(({ data }) => setSession(data.session))
      .catch(() => setSession(null))
      .finally(() => setLoading(false))

    // 2. Listener pour changes (login, logout, refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Resolve admin/owner role once per signed-in user. Most users are not
  // admins → a single is_admin() call (with JWT-stale refresh) settles it.
  // Only for confirmed admins do we make a second is_owner() call to refine
  // the label (owner ⊃ admin). UX hint only — server-side stays the boundary.
  const userId = session?.user?.id ?? null
  useEffect(() => {
    let cancelled = false
    // All setState lives inside the async callback (never synchronous in the
    // effect body) — mirrors the session effect's .then()/.finally() pattern
    // and satisfies react-hooks/set-state-in-effect.
    ;(async () => {
      if (!userId) {
        if (!cancelled) {
          setRole(null)
          setRoleLoading(false)
        }
        return
      }
      setRoleLoading(true)
      try {
        const adminResult = await checkIsAdminWithRefresh(supabase)
        if (cancelled) return
        if (adminResult !== 'allowed') {
          setRole(null)
          return
        }
        const { data: ownerData, error: ownerErr } = await supabase.rpc('is_owner')
        if (cancelled) return
        setRole(!ownerErr && ownerData === true ? 'owner' : 'admin')
      } catch {
        if (!cancelled) setRole(null)
      } finally {
        if (!cancelled) setRoleLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  async function signInWithMagicLink(email: string, options?: { next?: string; name?: string; marketingOptIn?: boolean }) {
    // Propage `next` en query param vers /auth/callback ; Supabase ne touche pas
    // la query string de `emailRedirectTo`, elle est conservée dans le lien email.
    // AuthCallback lira `?next=` et validera via resolveNext() (UI-SPEC §9).
    // Phase 96 (D-11) — la langue voyage dans le CHEMIN de rappel, jamais dans
    // `next`. AuthProvider est monté AU-DESSUS de BrowserRouter : useLocation()
    // ne lui est pas accessible, il lit donc window.location.pathname
    // directement — la langue ne change jamais sans navigation document (D-03),
    // elle est constante pour la durée du document, une lecture synchrone
    // suffit. Même raisonnement que LanguageProvider.
    const next = options?.next
    const prefix = langFromPath(window.location.pathname) ? EN_PREFIX : ''
    const callbackUrl = next
      ? `${window.location.origin}${prefix}/auth/callback?next=${encodeURIComponent(next)}`
      : `${window.location.origin}${prefix}/auth/callback`
    const otpOptions: Parameters<typeof supabase.auth.signInWithOtp>[0]['options'] = {
      emailRedirectTo: callbackUrl,
    }
    // Phase 114 (D-R5) — le choix de la case Login n'est lu par le trigger qu'à
    // l'INSERT (création du compte) ; absent ⇒ pas de clé ⇒ coché d'office.
    const data: Record<string, unknown> = {}
    if (options?.name) data.full_name = options.name
    if (options?.marketingOptIn !== undefined) data.marketing_opt_in = options.marketingOptIn
    if (Object.keys(data).length) otpOptions.data = data
    const { error } = await supabase.auth.signInWithOtp({ email, options: otpOptions })
    if (error) throw error
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider
      value={{ session, user: session?.user ?? null, loading, role, roleLoading, signInWithMagicLink, signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- Plan 01-03 locks the single-file convention: AuthProvider + useAuth ship together, no barrel export (CONVENTIONS.md)
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
